import { AddIcon, Button, DeleteIcon, Input, Select, SelectOptionType, showToast } from '@bp/ui-components';
import { FieldArray, Form, Formik, FormikHelpers } from 'formik';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { SingleValue } from 'react-select';
import { useAuthClaims } from '../../../hooks/useAuthClaims';
import { useCreateSelectOptions } from '../../../hooks/useCreateSelectOptions';
import { useMemorizedCacheTag } from '../../../hooks/useMemorizedCacheTag';
import { useSelectedSchoolYear } from '../../../hooks/useSelectedSchoolYear';
import {
  use_SchoolYearsQuery,
  useCreateTimetableMutation,
  useCreateTimetableVersionMutation,
  useTimeGridQuery,
  useUpdateTimetableMutation,
} from '../../../types/planung-graphql-client-defs';
import { partition } from '../../../utils/arrayFunc';
import { isConstraintError } from '../../../utils/formHelpers';
import { FormBlockHeader } from '../../Form/FormBlockHeader';
import { useTimetables } from '../hooks/useTimetables';
import { timetableSchema } from './validation/schema';

type TimetableFormProps = {
  timetableUuid: string | null;
  selectAllClasses: boolean;
  onClose: () => void;
};

type TimegridConfigsType = {
  timegridUuid: string | null;
  classes?: { uuid: string; name: string }[];
};

type TimetableFormType = {
  uuid: string;
  draft: boolean;
  name: string;
  comment: string;
  schoolYearUuid: string;
  timegridConfigs: TimegridConfigsType[];
};

export const TimetableForm = observer(({ timetableUuid, selectAllClasses, onClose }: TimetableFormProps) => {
  const { pimAuthClaims } = useAuthClaims();
  const { t } = useTranslation();
  const context = useMemorizedCacheTag('TIMETABLE');
  const currentSchoolYear = useSelectedSchoolYear();
  const [, create] = useCreateTimetableMutation();
  const [, update] = useUpdateTimetableMutation();
  const [, createVersion] = useCreateTimetableVersionMutation();

  // Alle Stundenpläne: der Name muss über alle Schuljahre eindeutig sein.
  const { timetables, refetch: reexecuteQuery } = useTimetables(undefined, { all: true });
  const [{ data: schoolYearsData }] = use_SchoolYearsQuery({});
  const [{ data: timeGridsData }] = useTimeGridQuery({});

  const [current, other] = partition(timetables ?? [], (elem) => elem.uuid === timetableUuid);
  const currentDraft: TimetableFormType =
    current.length > 0
      ? {
          uuid: current[0].uuid,
          name: current[0].name,
          draft: current[0].draft,
          comment: current[0].comment ?? '',
          schoolYearUuid: current[0].schoolYearUuid,
          timegridConfigs: current[0].timegridConfigs.map((tc) => ({ timegridUuid: tc.timegridUuid ?? '' })),
        }
      : {
          uuid: '',
          draft: true,
          name: '',
          comment: '',
          schoolYearUuid: currentSchoolYear?.uuid ?? '',
          timegridConfigs: [{ timegridUuid: timeGridsData?.timeGrids[0]?.uuid ?? '' }],
        };

  const schoolYearsOptions: SelectOptionType[] = useCreateSelectOptions(schoolYearsData?.schoolYears, 'uuid', 'name');
  const timeGridOptions: SelectOptionType[] = useCreateSelectOptions(timeGridsData?.timeGrids, 'uuid', 'name');

  // Die Auswahl ist schreibgeschützt und zeigt immer „für alle Klassen“.
  const classesOptions: SelectOptionType[] = [{ value: null, label: `(${t('common.all')})` }];

  const handleSubmit = async (values: TimetableFormType, formHelpers: FormikHelpers<TimetableFormType>) => {
    const input = {
      name: values.name,
      comment: values.comment,
      schoolYearUuid: values.schoolYearUuid,
      // die Position in der Liste ist die Reihenfolge der Raster-Zuordnungen
      timegridUuids: values.timegridConfigs.map((timegridConfig) => timegridConfig.timegridUuid ?? ''),
    };

    let mutationResult;
    if (timetableUuid) {
      // Das Zeitraster wird hier bewusst nicht angefasst: ein Wechsel müsste die gesteckten Karten und
      // Verfügbarkeiten mitziehen. Dafür gibt es „Kopieren nach → Neuer Stundenplan“ mit Stunden-Zuordnung.
      // Mitgeschickt werden deshalb die gespeicherten Raster, nicht die des Formulars.
      const timegridUuids = currentDraft.timegridConfigs.map((timegridConfig) => timegridConfig.timegridUuid ?? '');
      mutationResult = await update({ uuid: timetableUuid, input: { ...input, timegridUuids } }, context);
    } else {
      mutationResult = await create({ input }, context);

      // create timetable version with custom resolver
      const newTimetableUuid = mutationResult.data?.createTimetable.uuid;
      if (newTimetableUuid) {
        await createVersion(
          {
            timetableUuid: newTimetableUuid,
            organizationUuid: pimAuthClaims.getOrganizationUuid(),
          },
          context,
        );
      }
    }
    if (!mutationResult || mutationResult.error) {
      if (isConstraintError(mutationResult.error)) {
        reexecuteQuery({ requestPolicy: 'network-only' });
        await formHelpers.submitForm(); // trigger submit for revalidation
      }
    } else {
      showToast('Success', { type: 'success' });
      formHelpers.resetForm();
      onClose();
    }
  };

  return (
    <>
      <Formik
        initialValues={currentDraft}
        onSubmit={handleSubmit}
        validationSchema={() => {
          return timetableSchema(other.map((tt) => tt.name));
        }}
      >
        {({
          values,
          handleChange,
          resetForm,
          isSubmitting,
          isValidating,
          dirty,
          setFieldValue,
          setFieldTouched,
          errors,
          touched,
        }) => {
          return (
            <Form>
              <div className='tks__grid mt-6 mb-8'>
                <div className='tks__row row-xs-10'>
                  <div className='tks__col col-xs-6'>
                    <Input
                      label={t('common.name') as string}
                      name={'name'}
                      onChange={handleChange}
                      value={values.name}
                      {...(errors.name &&
                        touched.name && {
                          error: errors.name,
                        })}
                    />
                  </div>
                  <div className='tks__col col-xs-6'>
                    <Select
                      readonly={true}
                      label={t('common.schoolYear') as string}
                      options={schoolYearsOptions}
                      onChange={(option) => {
                        const opt = option as SingleValue<SelectOptionType>;
                        setFieldTouched('schoolYearUuid', true);
                        setFieldValue('schoolYearUuid', opt?.value, true);
                      }}
                      value={schoolYearsOptions.find((syo) => {
                        return syo.value === values.schoolYearUuid;
                      })}
                      name={'schoolYearUuid'}
                      {...(errors.schoolYearUuid &&
                        touched.schoolYearUuid && {
                          error: errors.schoolYearUuid,
                        })}
                    />
                  </div>
                </div>
                <div className='tks__row row-xs-10'>
                  <div className='tks__col col-xs'>
                    <Input
                      label={t('common.comment') as string}
                      name={'comment'}
                      onChange={handleChange}
                      value={values.comment ?? ''}
                      {...(errors.comment &&
                        touched.comment && {
                          error: errors.comment,
                        })}
                    />
                  </div>
                </div>
              </div>
              <FieldArray
                name={'timegridConfigs'}
                render={(arrayHelpers) => {
                  const ArrayErrors = () =>
                    typeof errors.timegridConfigs === 'string' && touched.timegridConfigs ? (
                      <div className={'error-message'}>{errors.timegridConfigs}</div>
                    ) : (
                      <></>
                    );
                  return (
                    <div className={'mb-10'}>
                      <FormBlockHeader title={t('timeGrid.title', { count: 2 })} />
                      {values.timegridConfigs?.map((timegridConfig, index) => {
                        return (
                          <div key={timegridConfig.timegridUuid} className={'tks__grid no-wrap'}>
                            <div className={'tks__row'}>
                              <div className={'tks__col col-xs-5'}>
                                <Select
                                  label={t('timeGrid.title', { count: 2 }) as string}
                                  name={`timegridConfigs.${index}.timegridUuid`}
                                  options={timeGridOptions}
                                  readonly={!!timetableUuid}
                                  onChange={(option) => {
                                    const opt = option as SingleValue<SelectOptionType>;
                                    setFieldTouched(`timegridConfigs.${index}.timegridUuid`, true);
                                    setFieldValue(`timegridConfigs.${index}.timegridUuid`, opt?.value, true);
                                  }}
                                  value={timeGridOptions.find((tgo) => {
                                    return tgo.value === values.timegridConfigs[index].timegridUuid;
                                  })}
                                />
                              </div>
                              <div className={'tks__col col-xs-5'}>
                                <Select
                                  label={t('timeGrid.validFor') as string}
                                  name={`timegridConfigs.${index}.classes`}
                                  isMulti={!selectAllClasses}
                                  readonly
                                  value={{ value: null, label: t('timeGrid.forAllClasses') }}
                                  onChange={(option) => {
                                    const opt = option as SingleValue<SelectOptionType>;
                                    setFieldTouched(`timegridConfigs.${index}.classes`, true);
                                    setFieldValue(`timegridConfigs.${index}.classes`, opt?.value, true);
                                  }}
                                  options={classesOptions}
                                />
                              </div>
                              <div className={'tks__col col-xs-2'}>
                                <Button
                                  className='mt-5 align-end'
                                  hierarchy={'tertiary'}
                                  icon={<DeleteIcon />}
                                  disabled={true}
                                  onClick={() => arrayHelpers.remove(index)}
                                />
                              </div>
                            </div>
                          </div>
                        );
                      })}
                      {
                        <Button
                          hierarchy={'tertiary'}
                          disabled={values.timegridConfigs.length > 0}
                          icon={<AddIcon />}
                          onClick={() => arrayHelpers.push({ timegridUuid: timeGridOptions[0].value })}
                        >
                          {t('timeGrid.title', { count: 2 })}
                        </Button>
                      }
                      {timetableUuid && <p className='mt-4'>{t('timeGrid.migration.formHint')}</p>}
                      <ArrayErrors />
                    </div>
                  );
                }}
              />
              <div className={'modal-bottom'}>
                <Button
                  hierarchy='tertiary'
                  type='button'
                  onClick={() => {
                    resetForm();
                    onClose();
                  }}
                  className={'buttons'}
                >
                  {t('common.cancelChanges')}
                </Button>
                <Button type='submit' disabled={isSubmitting || !dirty || isValidating} className={'buttons'}>
                  {t('common.save')}
                </Button>
              </div>
            </Form>
          );
        }}
      </Formik>
    </>
  );
});
